import { API } from 'aws-amplify'

import { fileControl } from '../graphql/queries'

// S3 erzwingt das Limit selbst (content-length-range in der Policy),
// siehe fileControl getUploadUrl im Backend
export const MAX_DOCUMENT_SIZE = 20 * 1024 * 1024 // 20 MB

export class DocumentUploadError extends Error {
  constructor(message, { stage, status, code, file_name } = {}) {
    super(message)
    this.name = 'DocumentUploadError'
    this.stage = stage
    this.status = status
    this.code = code
    this.file_name = file_name
  }
}

export const getDocumentFileName = ({ label, file }) =>
  `${(label || '').replace(/\s+/g, '_')}-${Date.now()}-${file.name}`

const getErrorMessage = (err) => {
  if (err instanceof Error) return err.message
  // API.graphql wirft bei GraphQL-Fehlern ein Objekt { errors: [...] }
  if (Array.isArray(err?.errors) && err.errors[0]?.message) {
    return err.errors[0].message
  }
  return String(err)
}

const getUploadUrl = async ({ space_id, file_name }) => {
  let raw
  try {
    raw = await API.graphql({
      query: fileControl,
      authMode: 'AWS_IAM',
      variables: { action: 'getUploadUrl', space_id, file_name }
    })
  } catch (err) {
    throw new DocumentUploadError(
      `getUploadUrl failed: ${getErrorMessage(err)}`,
      { stage: 'getUploadUrl', file_name }
    )
  }

  let statusCode, data
  try {
    const parsed = JSON.parse(raw.data.fileControl)
    statusCode = parsed.statusCode
    data = JSON.parse(parsed.body || '{}')
  } catch (err) {
    throw new DocumentUploadError(
      `getUploadUrl failed: invalid response (${getErrorMessage(err)})`,
      { stage: 'getUploadUrl', file_name }
    )
  }

  if (statusCode !== 200) {
    throw new DocumentUploadError(
      `getUploadUrl failed (${statusCode}): ${data.error || 'unknown'}`,
      { stage: 'getUploadUrl', status: statusCode, file_name }
    )
  }

  return data
}

const postToS3 = async ({ url, fields, file, file_name }) => {
  const form = new FormData()
  // Reihenfolge ist Pflicht: alle Policy-Felder zuerst, die Datei zuletzt
  Object.entries(fields).forEach(([key, value]) => form.append(key, value))
  form.append('file', file)

  let response
  try {
    // Content-Type-Header setzt FormData selbst (inkl. boundary)
    response = await fetch(url, { method: 'POST', body: form })
  } catch (err) {
    throw new DocumentUploadError(`S3 upload failed: ${getErrorMessage(err)}`, {
      stage: 's3',
      file_name
    })
  }

  if (response.status === 204) return

  let code
  try {
    const text = await response.text()
    code = (text.match(/<Code>([^<]+)<\/Code>/) || [])[1]
  } catch (err) {
    // Body ist nur Zusatzinfo
  }

  throw new DocumentUploadError(
    `S3 upload failed (${response.status}${code ? ` ${code}` : ''})`,
    { stage: 's3', status: response.status, code, file_name }
  )
}

// Holt direkt vor dem Upload eine presigned POST-URL und laedt die Datei an
// S3. Bei 403 (Policy abgelaufen oder Felder veraendert) wird einmal mit
// einer frischen URL wiederholt.
export const uploadDocument = async ({ space_id, file_name, file }) => {
  if (file.size > MAX_DOCUMENT_SIZE) {
    throw new DocumentUploadError('File too large', {
      stage: 'client',
      code: 'EntityTooLarge',
      file_name
    })
  }

  for (let attempt = 0; attempt < 2; attempt++) {
    const { url, fields } = await getUploadUrl({ space_id, file_name })

    try {
      await postToS3({ url, fields, file, file_name })
      return
    } catch (err) {
      if (attempt === 0 && err.status === 403) continue
      throw err
    }
  }
}

// Laedt alle Dateien aller Datei-Felder hoch. Ein Fehler bricht nicht die
// restlichen Uploads ab, sondern wird gesammelt und zurueckgegeben.
export const uploadDocuments = async ({ space_id, fields, values }) => {
  const failed = []

  for (const field of fields) {
    if (field.type !== 'file') continue

    for (const file of values[field.id] || []) {
      const file_name = getDocumentFileName({ label: field.label, file })

      try {
        await uploadDocument({ space_id, file_name, file })
      } catch (error) {
        failed.push({ field, file, file_name, error })
      }
    }
  }

  return { failed }
}
